import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  MenuItem,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import { PromptDataType, PromptModel } from "../types/prompt-model";
import {
  AssemblyReferenceConditionWrite,
  AssemblyReferenceCategoryConditionWrite,
  RangeComparisonOperator,
} from "../types/assembly-condition";

/**
 * A condition being edited in the UI. `_key` is a client-only stable identity
 * for the React list — it is NOT sent to the server (lineage is set only by the
 * clone pipeline), so it must be stripped before the condition is written.
 */
export interface EditableCondition extends AssemblyReferenceConditionWrite {
  _key: string;
}

/**
 * A blank condition with a fresh client-only key, defaulting the target
 * prompt to the reference's own prompt.
 */
export const createEmptyCondition = (
  ownPromptObjectIdentifier: string
): EditableCondition => ({
  isYes: null,
  fuzzyMatch: false,
  minimumDecimalAnswer: null,
  maximumDecimalAnswer: null,
  textAnswer: null,
  allCategoriesMatch: false,
  decimalAnswer: null,
  decimalOperator: null,
  dateOperator: null,
  startDate: null,
  endDate: null,
  dateAnswer: null,
  _key: uuidv4(),
  targetPromptObjectIdentifier: ownPromptObjectIdentifier,
  categoryConditions: [],
});

const OPERATOR_LABELS: Record<RangeComparisonOperator, string> = {
  [RangeComparisonOperator.Equal]: "Equal to",
  [RangeComparisonOperator.GreaterThan]: "Greater than",
  [RangeComparisonOperator.LessThan]: "Less than",
  [RangeComparisonOperator.GreaterThanOrEqual]: "Greater than or equal",
  [RangeComparisonOperator.LessThanOrEqual]: "Less than or equal",
  [RangeComparisonOperator.Range]: "Between (range)",
};

const OPERATORS: RangeComparisonOperator[] = [
  RangeComparisonOperator.Equal,
  RangeComparisonOperator.GreaterThan,
  RangeComparisonOperator.LessThan,
  RangeComparisonOperator.GreaterThanOrEqual,
  RangeComparisonOperator.LessThanOrEqual,
  RangeComparisonOperator.Range,
];

interface ConditionsEditorProps {
  conditions: EditableCondition[];
  onChange: (conditions: EditableCondition[]) => void;
  /** Reference's own prompt — the default target for new conditions. */
  ownPromptObjectIdentifier: string;
  /** Group the reference's prompt belongs to. Drives the target-prompt picker. */
  groupObjectIdentifier?: string;
  /**
   * Prompts in the same group as the reference's prompt. Used to render the
   * target-prompt picker and to look up each target's data type so the
   * condition fields match.
   */
  groupPrompts: PromptModel[];
  /** Fallback data type when a target prompt isn't found in `groupPrompts`. */
  ownPromptDataType?: PromptDataType;
}

/**
 * Editor for the conditions attached to an Assembly Reference. Each condition
 * targets a prompt in the same group as the reference's prompt (defaults to
 * the reference's own prompt); the input fields shown adapt to the target
 * prompt's data type.
 */
const ConditionsEditor = ({
  conditions,
  onChange,
  ownPromptObjectIdentifier,
  groupObjectIdentifier,
  groupPrompts,
  ownPromptDataType,
}: ConditionsEditorProps) => {
  const promptByObjectIdentifier = React.useMemo(() => {
    const map = new Map<string, PromptModel>();
    for (const p of groupPrompts) {
      map.set(p.objectIdentifier, p);
    }
    return map;
  }, [groupPrompts]);

  const resolveDataType = (targetPromptObjectIdentifier: string): PromptDataType | undefined =>
    promptByObjectIdentifier.get(targetPromptObjectIdentifier)?.promptDataTypeId
    ?? (targetPromptObjectIdentifier === ownPromptObjectIdentifier ? ownPromptDataType : undefined);

  const patch = (index: number, change: Partial<AssemblyReferenceConditionWrite>) => {
    onChange(conditions.map((c, i) => (i === index ? { ...c, ...change } : c)));
  };

  const remove = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...conditions, createEmptyCondition(ownPromptObjectIdentifier)]);
  };

  // `ownPromptObjectIdentifier` is "" before the user has picked a prompt in
  // add mode. In that state there's no sensible default target for a new
  // condition, so we show the same hint the editor used to show.
  if (!ownPromptObjectIdentifier) {
    return (
      <Typography variant="caption" color="textSecondary">
        Select a prompt to add conditions.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {conditions.map((condition, index) => {
        const targetDataType = resolveDataType(condition.targetPromptObjectIdentifier);
        return (
          <Box
            key={condition._key}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="textSecondary">
                Condition {index + 1}
              </Typography>
              <IconButton size="small" color="error" onClick={() => remove(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
            <TargetPromptPicker
              value={condition.targetPromptObjectIdentifier}
              ownPromptObjectIdentifier={ownPromptObjectIdentifier}
              groupObjectIdentifier={groupObjectIdentifier}
              groupPrompts={groupPrompts}
              onChange={(targetPromptObjectIdentifier) =>
                // When the target changes, the new prompt's data type may differ —
                // reset every data-type-specific field so the saved condition matches
                // what the new field shape will let the user enter.
                patch(index, {
                  targetPromptObjectIdentifier,
                  isYes: null,
                  textAnswer: null,
                  fuzzyMatch: false,
                  decimalAnswer: null,
                  decimalOperator: null,
                  minimumDecimalAnswer: null,
                  maximumDecimalAnswer: null,
                  dateOperator: null,
                  startDate: null,
                  endDate: null,
                  dateAnswer: null,
                  allCategoriesMatch: false,
                  categoryConditions: [],
                })
              }
            />
            {targetDataType !== undefined ? (
              <Box sx={{ mt: 1.5 }}>
                <ConditionFields
                  condition={condition}
                  promptDataType={targetDataType}
                  onPatch={(change) => patch(index, change)}
                />
              </Box>
            ) : (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5, display: "block" }}>
                Pick a target prompt to configure this condition.
              </Typography>
            )}
          </Box>
        );
      })}
      <Button startIcon={<Add />} variant="outlined" size="small" onClick={add}>
        Add condition
      </Button>
    </Stack>
  );
};

interface TargetPromptPickerProps {
  value: string;
  ownPromptObjectIdentifier: string;
  groupObjectIdentifier?: string;
  groupPrompts: PromptModel[];
  onChange: (value: string) => void;
}

/**
 * Per-condition picker for the prompt this condition is evaluated against.
 * Lists prompts in the reference's group, with the reference's own prompt
 * flagged. When the group hasn't been loaded (edit mode without group context)
 * the picker degrades to a read-only label so the user at least sees which
 * prompt the saved condition targets.
 */
const TargetPromptPicker = ({
  value,
  ownPromptObjectIdentifier,
  groupObjectIdentifier,
  groupPrompts,
  onChange,
}: TargetPromptPickerProps) => {
  if (!groupObjectIdentifier || groupPrompts.length === 0) {
    const targetLabel =
      groupPrompts.find((p) => p.objectIdentifier === value)?.label
      ?? (value === ownPromptObjectIdentifier ? "This reference's prompt" : "—");
    return (
      <Typography variant="caption" color="textSecondary">
        Apply when: <strong>{targetLabel}</strong>
      </Typography>
    );
  }

  return (
    <TextField
      select
      fullWidth
      size="small"
      label="Apply when"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {groupPrompts.map((prompt) => (
        <MenuItem key={prompt.objectIdentifier} value={prompt.objectIdentifier}>
          {prompt.label}
          {prompt.objectIdentifier === ownPromptObjectIdentifier ? " (this reference)" : ""}
        </MenuItem>
      ))}
    </TextField>
  );
};

interface ConditionFieldsProps {
  condition: AssemblyReferenceConditionWrite;
  promptDataType: PromptDataType;
  onPatch: (change: Partial<AssemblyReferenceConditionWrite>) => void;
}

/** Renders the data-type-specific inputs for a single condition. */
const ConditionFields = ({ condition, promptDataType, onPatch }: ConditionFieldsProps) => {
  switch (promptDataType) {
    case PromptDataType.Boolean:
      return (
        <FormControl>
          <FormLabel sx={{ fontSize: "0.8rem", mb: 0.5 }}>Expected Answer</FormLabel>
          <RadioGroup
            value={condition.isYes === null || condition.isYes === undefined ? "" : String(condition.isYes)}
            onChange={(e) => onPatch({ isYes: e.target.value === "true" })}
          >
            <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
            <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
          </RadioGroup>
        </FormControl>
      );

    case PromptDataType.RangeOfValues:
      return (
        <Stack spacing={1}>
          <TextField
            select
            fullWidth
            size="small"
            label="Comparison"
            value={condition.decimalOperator ?? ""}
            onChange={(e) =>
              onPatch({ decimalOperator: (e.target.value || null) as RangeComparisonOperator | null })
            }
          >
            {OPERATORS.map((op) => (
              <MenuItem key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </MenuItem>
            ))}
          </TextField>
          {condition.decimalOperator === RangeComparisonOperator.Range ? (
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Minimum"
                value={condition.minimumDecimalAnswer ?? ""}
                onChange={(e) =>
                  onPatch({ minimumDecimalAnswer: e.target.value === "" ? null : Number(e.target.value) })
                }
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Maximum"
                value={condition.maximumDecimalAnswer ?? ""}
                onChange={(e) =>
                  onPatch({ maximumDecimalAnswer: e.target.value === "" ? null : Number(e.target.value) })
                }
              />
            </Stack>
          ) : (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Value"
              value={condition.decimalAnswer ?? ""}
              onChange={(e) =>
                onPatch({ decimalAnswer: e.target.value === "" ? null : Number(e.target.value) })
              }
            />
          )}
        </Stack>
      );

    case PromptDataType.Date:
      return (
        <Stack spacing={1}>
          <TextField
            select
            fullWidth
            size="small"
            label="Comparison"
            value={condition.dateOperator ?? ""}
            onChange={(e) =>
              onPatch({ dateOperator: (e.target.value || null) as RangeComparisonOperator | null })
            }
          >
            {OPERATORS.map((op) => (
              <MenuItem key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </MenuItem>
            ))}
          </TextField>
          {condition.dateOperator === RangeComparisonOperator.Range ? (
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                slotProps={{ inputLabel: { shrink: true } }}
                value={condition.startDate ?? ""}
                onChange={(e) => onPatch({ startDate: e.target.value || null })}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                slotProps={{ inputLabel: { shrink: true } }}
                value={condition.endDate ?? ""}
                onChange={(e) => onPatch({ endDate: e.target.value || null })}
              />
            </Stack>
          ) : (
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={condition.dateAnswer ?? ""}
              onChange={(e) => onPatch({ dateAnswer: e.target.value || null })}
            />
          )}
        </Stack>
      );

    case PromptDataType.ListOfValues:
      return (
        <CategoryConditionsFields
          categoryConditions={condition.categoryConditions}
          allCategoriesMatch={condition.allCategoriesMatch}
          onChange={(categoryConditions, allCategoriesMatch) =>
            onPatch({ categoryConditions, allCategoriesMatch })
          }
        />
      );

    default:
      // Text and Generative prompts
      return (
        <Stack spacing={1}>
          <TextField
            fullWidth
            size="small"
            label="Answer contains"
            value={condition.textAnswer ?? ""}
            onChange={(e) => onPatch({ textAnswer: e.target.value || null })}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={condition.fuzzyMatch}
                onChange={(e) => onPatch({ fuzzyMatch: e.target.checked })}
              />
            }
            label="Fuzzy match"
          />
        </Stack>
      );
  }
};

interface CategoryConditionsFieldsProps {
  categoryConditions: AssemblyReferenceCategoryConditionWrite[];
  allCategoriesMatch: boolean;
  onChange: (
    categoryConditions: AssemblyReferenceCategoryConditionWrite[],
    allCategoriesMatch: boolean
  ) => void;
}

const CategoryConditionsFields = ({
  categoryConditions,
  allCategoriesMatch,
  onChange,
}: CategoryConditionsFieldsProps) => {
  const updateCategory = (index: number, change: Partial<AssemblyReferenceCategoryConditionWrite>) =>
    onChange(
      categoryConditions.map((c, i) => (i === index ? { ...c, ...change } : c)),
      allCategoriesMatch
    );

  return (
    <Stack spacing={1}>
      {categoryConditions.map((category, index) => (
        <Stack direction="row" spacing={1} alignItems="center" key={index}>
          <TextField
            fullWidth
            size="small"
            label={`Category ${index + 1}`}
            value={category.text}
            onChange={(e) => updateCategory(index, { text: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={category.fuzzyMatch}
                onChange={(e) => updateCategory(index, { fuzzyMatch: e.target.checked })}
              />
            }
            label="Fuzzy"
          />
          <IconButton
            size="small"
            color="error"
            onClick={() =>
              onChange(
                categoryConditions.filter((_, i) => i !== index),
                allCategoriesMatch
              )
            }
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        startIcon={<Add />}
        size="small"
        onClick={() =>
          onChange([...categoryConditions, { text: "", fuzzyMatch: false }], allCategoriesMatch)
        }
      >
        Add category
      </Button>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={allCategoriesMatch}
            onChange={(e) => onChange(categoryConditions, e.target.checked)}
          />
        }
        label="All categories must match"
      />
    </Stack>
  );
};

export default ConditionsEditor;
